import { Text } from '@lmn/design-system';
import styled from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';

/**
 * Why a room card shows the grey placeholder instead of a photo (MPH-1321): the room arrived with no
 * images, or it arrived with photo urls that all failed to load.
 */
export type RoomImageFallbackReason = 'noImages' | 'failedToLoad';

// MPH-1321: a room that came with no photos says the hotel provided none; photos that failed to load
// keep the hero gallery's generic "no images" copy, as the hotel did provide them. That generic copy is
// also the fallback for a language still missing the newer key (Danish, as of October 2026), whose
// caption would otherwise be an empty box.
const CAPTION_KEYS: Record<RoomImageFallbackReason, string> = {
  noImages: 'vacation.select.cc.rooms.no_photos',
  failedToLoad: 'vacation.select.details.gallery.noimages',
};

/**
 * The caption to show over a room's image, if any. A room without photos is decided from the data, not
 * from the image's own fallback state (the legacy mobile panel's placeholder url itself falls back). A
 * Top Secret stay never gets one: its mark says the photo is withheld on purpose, not missing (MPH-935).
 */
export function getRoomImageFallbackReason({
  hasImages,
  photosFailed,
  isTopSecret,
}: {
  hasImages: boolean;
  photosFailed: boolean;
  isTopSecret: boolean;
}): RoomImageFallbackReason | null {
  if (isTopSecret) return null;
  if (!hasImages) return 'noImages';
  return photosFailed ? 'failedToLoad' : null;
}

// Centred over the placeholder; the parent image wrapper must be `position: relative`. The translucent
// overlay token (60% black) lets the placeholder show through, as in the ticket's mockup. The explicit
// max-content width is what lets the max-width apply: an auto width at left: 50% would cap the box at half
// the image.
const FallbackImageCaption = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: max-content;
  max-width: ${({ theme }) => `calc(100% - 2 * ${theme.kallisto.space.space200})`};
  padding: ${({ theme }) => `${theme.kallisto.space.space100} ${theme.kallisto.space.space200}`};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundOverlay};
  text-align: center;
  pointer-events: none;
`;

/**
 * The image wrappers are labelled buttons ("Show details - <room>"), whose label replaces their content as
 * the accessible name, so the caption text alone is never announced inside one. Give it an `id` and point
 * the button's `aria-describedby` at it, so screen readers read the caption as the button's description.
 */
export function RoomImageFallbackCaption({ reason, id }: { reason: RoomImageFallbackReason; id?: string }) {
  const { tWithFallback } = useTranslation();
  return (
    <FallbackImageCaption id={id}>
      <Text variant="BodySmall" color="textOnDark" forceTag="span">
        {tWithFallback(CAPTION_KEYS[reason], CAPTION_KEYS.failedToLoad)}
      </Text>
    </FallbackImageCaption>
  );
}
